const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;

/**
 * Calculate route distance between two points using Mapbox Directions API
 * @param {Object} origin - Origin point with location { latitude, longitude }
 * @param {Object} destination - Destination point with location { latitude, longitude }
 * @returns {Promise<number|null>} Distance in meters or null if failed
 */
export const getRouteDistance = async (origin, destination) => {
    if (!origin?.location || !destination?.location) {
        return null;
    }

    const { longitude: originLng, latitude: originLat } = origin.location;
    const { longitude: destLng, latitude: destLat } = destination.location;

    if (!originLng || !originLat || !destLng || !destLat) {
        return null;
    }

    try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${originLng},${originLat};${destLng},${destLat}?overview=false&access_token=${MAPBOX_ACCESS_TOKEN}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.routes?.[0]?.distance) {
            return Math.round(data.routes[0].distance);
        }

        return null;
    } catch (error) {
        console.error('Failed to calculate route distance:', error);
        return null;
    }
};

/**
 * Calculate route distance with waypoints
 * @param {Object} origin - Origin point
 * @param {Object} destination - Destination point
 * @param {Array} waypoints - Array of waypoint objects
 * @returns {Promise<number|null>} Total distance in meters
 */
export const getRouteDistanceWithWaypoints = async (origin, destination, waypoints = []) => {
    if (!origin?.location || !destination?.location) {
        return null;
    }

    const coords = [
        `${origin.location.longitude},${origin.location.latitude}`,
        ...waypoints
            .filter(wp => wp?.location?.longitude && wp?.location?.latitude)
            .map(wp => `${wp.location.longitude},${wp.location.latitude}`),
        `${destination.location.longitude},${destination.location.latitude}`
    ];

    if (coords.length < 2) {
        return null;
    }

    try {
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coords.join(';')}?overview=false&access_token=${MAPBOX_ACCESS_TOKEN}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.routes?.[0]?.distance) {
            return Math.round(data.routes[0].distance);
        }

        return null;
    } catch (error) {
        console.error('Failed to calculate route distance:', error);
        return null;
    }
};
